/**
 * Strategy calculation utilities for bulk analysis
 * Handles betting strategy application and performance metrics calculation
 */

/**
 * Apply a betting strategy to historical race data
 * @param {Array} races - Historical race data
 * @param {Object} strategy - Strategy configuration
 * @returns {Array} Races with strategy applications
 */
export const applyStrategyToRaces = (races, strategy) => {
  if (!races || !strategy) return [];
  
  // Ensure races is an array
  if (!Array.isArray(races)) {
    console.warn('applyStrategyToRaces - races is not an array:', races);
    return [];
  }

  return races.map(race => {
    const strategyApplication = calculateStrategyApplication(race, strategy);
    const calculatedMetrics = calculateRaceMetrics(strategyApplication);

    return {
      ...race,
      strategyApplications: [strategyApplication],
      calculatedMetrics
    };
  });
};

/**
 * Calculate strategy application for a single race
 * @param {Object} race - Race data
 * @param {Object} strategy - Strategy configuration
 * @returns {Object} Strategy application result
 */
export const calculateStrategyApplication = (race, strategy) => {
  const bets = [];
  let totalStake = 0;
  let totalReturn = 0;
  let applied = false;

  // Check if strategy rules are met
  if (shouldApplyStrategy(race, strategy)) {
    applied = true;

    // Apply bets based on tip selections
    strategy.tipSelections?.forEach(tipSelection => {
      if (tipSelection.enabled) {
        const bet = createBetFromTip(race, tipSelection, strategy);
        if (bet) {
          bets.push(bet);
          totalStake += bet.stake;
          totalReturn += bet.return;
        }
      }
    });
  }

  return {
    strategyId: strategy.id,
    applied,
    bets,
    profit: totalReturn - totalStake
  };
};

/**
 * Check if strategy should be applied to a race
 * @param {Object} race - Race data
 * @param {Object} strategy - Strategy configuration
 * @returns {boolean} Whether to apply strategy
 */
export const shouldApplyStrategy = (race, strategy) => {
  if (!race || !strategy) return false;
  
  // Check all strategy rules
  for (const rule of strategy.rules || []) {
    if (!evaluateStrategyRule(race, rule)) {
      return false;
    }
  }
  
  // Check if any tips are enabled
  const enabledTips = strategy.tipSelections?.filter(tip => tip.enabled) || [];
  if (enabledTips.length === 0) {
    return false;
  }
  
  return true;
};

/**
 * Evaluate a single strategy rule against race data
 * @param {Object} race - Race data
 * @param {Object} rule - Strategy rule
 * @returns {boolean} Whether rule is satisfied
 */
export const evaluateStrategyRule = (race, rule) => {
  switch (rule.type) {
    case 'odds_range':
      return evaluateOddsRangeRule(race, rule);
    case 'tip_position':
      return evaluateTipPositionRule(race, rule);
    case 'track_condition':
      return evaluateTrackConditionRule(race, rule);
    case 'horse_count':
      return evaluateHorseCountRule(race, rule);
    default:
      return true;
  }
};

/**
 * Evaluate odds range rule
 * @param {Object} race - Race data
 * @param {Object} rule - Odds range rule
 * @returns {boolean} Whether rule is satisfied
 */
export const evaluateOddsRangeRule = (race, rule) => {
  const { min, max } = rule.value || {};
  const combinedOdds = calculateCombinedOdds(race);
  
  if (min !== undefined && combinedOdds < min) return false;
  if (max !== undefined && combinedOdds > max) return false;
  
  return true;
};

/**
 * Evaluate tip position rule
 * @param {Object} race - Race data
 * @param {Object} rule - Tip position rule
 * @returns {boolean} Whether rule is satisfied
 */
export const evaluateTipPositionRule = (race, rule) => {
  // This would check if specific tip positions meet criteria
  // For now, return true as a placeholder
  return true;
};

/**
 * Evaluate track condition rule
 * @param {Object} race - Race data
 * @param {Object} rule - Track condition rule
 * @returns {boolean} Whether rule is satisfied
 */
export const evaluateTrackConditionRule = (race, rule) => {
  const trackCondition = race.trackCondition || race.track_condition;
  const allowedConditions = rule.value || [];
  
  if (!trackCondition || allowedConditions.length === 0) return true;
  
  return allowedConditions.includes(trackCondition.toLowerCase());
};

/**
 * Evaluate horse count rule
 * @param {Object} race - Race data
 * @param {Object} rule - Horse count rule
 * @returns {boolean} Whether rule is satisfied
 */
export const evaluateHorseCountRule = (race, rule) => {
  const horseCount = race.horses?.length || race.horse_count || 0;
  const { min, max } = rule.value || {};
  
  if (min !== undefined && horseCount < min) return false;
  if (max !== undefined && horseCount > max) return false;
  
  return true;
};

/**
 * Calculate combined odds for all tips in a race
 * @param {Object} race - Race data
 * @returns {number} Combined odds
 */
export const calculateCombinedOdds = (race) => {
  if (!race.tips || !Array.isArray(race.tips)) return 0;
  
  return race.tips.reduce((sum, tip) => {
    const odds = parseFloat(tip.odds) || 0;
    return sum + odds;
  }, 0);
};

/**
 * Create a bet from a tip selection
 * @param {Object} race - Race data
 * @param {Object} tipSelection - Tip selection configuration
 * @param {Object} strategy - Strategy configuration
 * @returns {Object|null} Bet object or null if no bet
 */
export const createBetFromTip = (race, tipSelection, strategy) => {
  if (!tipSelection.enabled) return null;
  
  const stake = calculateStake(strategy, tipSelection);
  const tipData = getTipData(race, tipSelection.position);
  
  if (!tipData || !tipData.odds || tipData.odds <= 0) return null;

  // Determine bet result based on actual race results if available
  const result = determineBetResult(race, tipData, 'win'); // Default to win betting
  const returnAmount = result === 'win' ? stake * tipData.odds : 0;

  return {
    tipPosition: tipSelection.position,
    horseNumber: tipData.horseNumber || tipData.horse_number || 'N/A',
    horseName: tipData.horseName || tipData.horse_name || 'Unknown',
    odds: tipData.odds,
    stake,
    result,
    return: returnAmount,
    profit: returnAmount - stake
  };
};

/**
 * Get tip data for a specific position
 * @param {Object} race - Race data
 * @param {string} position - Tip position (T1, T2, etc.)
 * @returns {Object|null} Tip data or null if not found
 */
export const getTipData = (race, position) => {
  if (!race.tips || !Array.isArray(race.tips)) return null;
  
  // Convert position to index (T1 = 0, T2 = 1, etc.)
  const tipIndex = parseInt(position.replace('T', '')) - 1;
  
  if (tipIndex < 0 || tipIndex >= race.tips.length) return null;
  
  return race.tips[tipIndex];
};

/**
 * Determine bet result based on race outcome
 * @param {Object} race - Race data
 * @param {Object} tipData - Tip data
 * @param {string} betType - Type of bet ('win', 'place', 'each_way')
 * @returns {string} Bet result ('win', 'place', 'loss')
 */
export const determineBetResult = (race, tipData, betType = 'win') => {
  // If no race results available, simulate based on odds
  if (!race.podium || !Array.isArray(race.podium)) {
    return simulateBetResult(tipData.odds);
  }
  
  const horseNumber = tipData.horseNumber || tipData.horse_number;
  if (!horseNumber) return 'loss';
  
  switch (betType) {
    case 'win':
      return race.podium[0] === horseNumber ? 'win' : 'loss';
    case 'place':
      return race.podium.slice(0, 3).includes(horseNumber) ? 'win' : 'loss';
    case 'each_way':
      if (race.podium[0] === horseNumber) return 'win';
      if (race.podium.slice(0, 3).includes(horseNumber)) return 'place';
      return 'loss';
    default:
      return 'loss';
  }
};

/**
 * Simulate bet result based on odds (for races without results)
 * @param {number} odds - Bet odds
 * @returns {string} Simulated result
 */
export const simulateBetResult = (odds) => {
  if (!odds || odds <= 0) return 'loss';
  
  // Convert odds to implied probability and add some randomness
  const impliedProbability = 1 / odds;
  const adjustedProbability = Math.min(impliedProbability * 0.85, 0.95); // Slightly reduce to account for bookmaker margin
  
  return Math.random() < adjustedProbability ? 'win' : 'loss';
};

/**
 * Calculate stake amount based on strategy and tip selection
 * @param {Object} strategy - Strategy configuration
 * @param {Object} tipSelection - Tip selection
 * @param {Object} context - Additional context (current balance, streak, etc.)
 * @returns {number} Stake amount
 */
export const calculateStake = (strategy, tipSelection, context = {}) => {
  const baseStake = strategy.unitSize * (tipSelection.weight || 1);
  
  switch (strategy.unitSystem) {
    case 'fixed':
      return baseStake;
    case 'perry':
      return calculatePerryStake(baseStake, context);
    case 'custom':
      return calculateCustomStake(baseStake, strategy, context);
    default:
      return baseStake;
  }
};

/**
 * Calculate Perry system stake
 * @param {number} baseStake - Base stake amount
 * @param {Object} context - Betting context
 * @returns {number} Perry system stake
 */
export const calculatePerryStake = (baseStake, context) => {
  const { currentStreak = 0, streakType = 'none' } = context;
  
  // Perry system: increase stake after losses, reset after wins
  if (streakType === 'loss') {
    const multiplier = Math.min(Math.pow(2, Math.min(currentStreak, 4)), 16); // Cap at 16x
    return baseStake * multiplier;
  }
  
  return baseStake;
};

/**
 * Calculate custom stake based on strategy rules
 * @param {number} baseStake - Base stake amount
 * @param {Object} strategy - Strategy configuration
 * @param {Object} context - Betting context
 * @returns {number} Custom stake amount
 */
export const calculateCustomStake = (baseStake, strategy, context) => {
  // TODO: Implement custom staking rules based on strategy.rules
  // For now, return base stake
  return baseStake;
};

/**
 * Get odds for a specific tip position
 * @param {Object} race - Race data
 * @param {string} tipPosition - Tip position (T1, T2, etc.)
 * @returns {number|null} Odds or null if not available
 */
export const getOddsForTip = (race, tipPosition) => {
  const tipData = getTipData(race, tipPosition);
  if (!tipData) return null;
  
  const odds = parseFloat(tipData.odds);
  return isNaN(odds) || odds <= 0 ? null : odds;
};

/**
 * Calculate race-level metrics from strategy application
 * @param {Object} strategyApplication - Strategy application result
 * @returns {Object} Race metrics
 */
export const calculateRaceMetrics = (strategyApplication) => {
  const totalStake = strategyApplication.bets.reduce((sum, bet) => sum + bet.stake, 0);
  const totalReturn = strategyApplication.bets.reduce((sum, bet) => sum + bet.return, 0);
  const profit = totalReturn - totalStake;
  const roi = totalStake > 0 ? profit / totalStake : 0;

  return {
    totalStake,
    totalReturn,
    profit,
    roi
  };
};

/**
 * Calculate overall performance metrics from race results
 * @param {Array} raceResults - Array of race results with strategy applications
 * @returns {Object} Performance metrics
 */
export const calculatePerformanceMetrics = (raceResults) => {
  if (!raceResults || raceResults.length === 0) {
    return createEmptyPerformanceMetrics();
  }

  // Calculate cumulative profits for each race
  const racesWithCumulative = calculateCumulativeProfits(raceResults);
  
  const allBets = racesWithCumulative.flatMap(race => 
    race.strategyApplications?.flatMap(app => app.bets) || []
  );

  const totalBets = allBets.length;
  const totalStake = allBets.reduce((sum, bet) => sum + bet.stake, 0);
  const totalReturns = allBets.reduce((sum, bet) => sum + bet.return, 0);
  const netProfit = totalReturns - totalStake;
  const roi = totalStake > 0 ? netProfit / totalStake : 0;

  const winningBets = allBets.filter(bet => bet.result === 'win' || bet.result === 'place');
  const losingBets = allBets.filter(bet => bet.result === 'loss');
  
  const winRate = totalBets > 0 ? winningBets.length / totalBets : 0;
  const strikeRate = winRate;

  const averageWin = winningBets.length > 0 
    ? winningBets.reduce((sum, bet) => sum + bet.profit, 0) / winningBets.length 
    : 0;
  
  const averageLoss = losingBets.length > 0 
    ? Math.abs(losingBets.reduce((sum, bet) => sum + bet.profit, 0) / losingBets.length)
    : 0;

  const profitFactor = averageLoss > 0 && winningBets.length > 0 
    ? (averageWin * winningBets.length) / (averageLoss * losingBets.length) 
    : 0;

  // Calculate advanced metrics
  const { maxDrawdown, maxDrawdownDuration } = calculateDrawdown(racesWithCumulative);
  const { longestWinStreak, longestLossStreak } = calculateStreaks(allBets);
  const sharpeRatio = calculateSharpeRatio(racesWithCumulative);
  
  // Additional metrics
  const averageRoi = calculateAverageRoi(racesWithCumulative);
  const volatility = calculateVolatility(racesWithCumulative);
  const calmarRatio = maxDrawdown > 0 ? roi / maxDrawdown : 0;

  return {
    totalBets,
    totalReturns,
    netProfit,
    roi,
    winRate,
    strikeRate,
    maxDrawdown,
    maxDrawdownDuration,
    sharpeRatio,
    profitFactor,
    averageWin,
    averageLoss,
    longestWinStreak,
    longestLossStreak,
    averageRoi,
    volatility,
    calmarRatio,
    totalStake
  };
};

/**
 * Create empty performance metrics object
 * @returns {Object} Empty performance metrics
 */
export const createEmptyPerformanceMetrics = () => ({
  totalBets: 0,
  totalReturns: 0,
  netProfit: 0,
  roi: 0,
  winRate: 0,
  strikeRate: 0,
  maxDrawdown: 0,
  maxDrawdownDuration: 0,
  sharpeRatio: 0,
  profitFactor: 0,
  averageWin: 0,
  averageLoss: 0,
  longestWinStreak: 0,
  longestLossStreak: 0,
  averageRoi: 0,
  volatility: 0,
  calmarRatio: 0,
  totalStake: 0
});

/**
 * Calculate cumulative profits for race results
 * @param {Array} raceResults - Race results
 * @returns {Array} Race results with cumulative profits
 */
export const calculateCumulativeProfits = (raceResults) => {
  let cumulativeProfit = 0;
  
  return raceResults.map((race, index) => {
    const raceProfit = race.calculatedMetrics?.profit || 0;
    cumulativeProfit += raceProfit;
    
    return {
      ...race,
      cumulativeProfit,
      raceIndex: index
    };
  });
};

/**
 * Calculate average ROI across races
 * @param {Array} raceResults - Race results with cumulative data
 * @returns {number} Average ROI
 */
export const calculateAverageRoi = (raceResults) => {
  const raceRois = raceResults
    .map(race => race.calculatedMetrics?.roi || 0)
    .filter(roi => !isNaN(roi));
  
  if (raceRois.length === 0) return 0;
  
  return raceRois.reduce((sum, roi) => sum + roi, 0) / raceRois.length;
};

/**
 * Calculate volatility of returns
 * @param {Array} raceResults - Race results with cumulative data
 * @returns {number} Volatility (standard deviation of returns)
 */
export const calculateVolatility = (raceResults) => {
  const returns = raceResults.map(race => race.calculatedMetrics?.roi || 0);
  
  if (returns.length < 2) return 0;
  
  const mean = returns.reduce((sum, ret) => sum + ret, 0) / returns.length;
  const variance = returns.reduce((sum, ret) => sum + Math.pow(ret - mean, 2), 0) / returns.length;
  
  return Math.sqrt(variance);
};

/**
 * Calculate maximum drawdown and duration
 * @param {Array} raceResults - Race results with cumulative profits
 * @returns {Object} Drawdown metrics
 */
export const calculateDrawdown = (raceResults) => {
  let maxDrawdown = 0;
  let maxDrawdownDuration = 0;
  let peak = 0;
  let cumulativeProfit = 0;
  let drawdownStart = null;

  raceResults.forEach((race, index) => {
    const raceProfit = race.calculatedMetrics?.profit || 0;
    cumulativeProfit += raceProfit;

    if (cumulativeProfit > peak) {
      peak = cumulativeProfit;
      drawdownStart = null;
    } else {
      const currentDrawdown = peak - cumulativeProfit;
      if (currentDrawdown > maxDrawdown) {
        maxDrawdown = currentDrawdown;
      }
      
      if (drawdownStart === null) {
        drawdownStart = index;
      } else {
        const currentDuration = index - drawdownStart;
        if (currentDuration > maxDrawdownDuration) {
          maxDrawdownDuration = currentDuration;
        }
      }
    }
  });

  return { maxDrawdown, maxDrawdownDuration };
};

/**
 * Calculate winning and losing streaks
 * @param {Array} bets - Array of bet results
 * @returns {Object} Streak metrics
 */
export const calculateStreaks = (bets) => {
  let longestWinStreak = 0;
  let longestLossStreak = 0;
  let currentWinStreak = 0;
  let currentLossStreak = 0;

  bets.forEach(bet => {
    if (bet.result === 'win') {
      currentWinStreak++;
      currentLossStreak = 0;
      longestWinStreak = Math.max(longestWinStreak, currentWinStreak);
    } else {
      currentLossStreak++;
      currentWinStreak = 0;
      longestLossStreak = Math.max(longestLossStreak, currentLossStreak);
    }
  });

  return { longestWinStreak, longestLossStreak };
};

/**
 * Calculate Sharpe ratio (simplified)
 * @param {Array} raceResults - Race results
 * @returns {number} Sharpe ratio
 */
export const calculateSharpeRatio = (raceResults) => {
  if (!raceResults || raceResults.length < 2) return 0;

  const returns = raceResults.map(race => race.calculatedMetrics?.roi || 0);
  const avgReturn = returns.reduce((sum, ret) => sum + ret, 0) / returns.length;
  
  const variance = returns.reduce((sum, ret) => sum + Math.pow(ret - avgReturn, 2), 0) / returns.length;
  const stdDev = Math.sqrt(variance);
  
  return stdDev > 0 ? avgReturn / stdDev : 0;
};

/**
 * Compare two strategies and calculate performance differences
 * @param {Object} strategy1Results - First strategy results
 * @param {Object} strategy2Results - Second strategy results
 * @returns {Array} Array of comparison metrics
 */
export const compareStrategies = (strategy1Results, strategy2Results) => {
  const metrics1 = strategy1Results.performance;
  const metrics2 = strategy2Results.performance;
  
  const comparisonMetrics = [
    {
      name: 'Total Profit',
      strategy1Value: metrics1.netProfit,
      strategy2Value: metrics2.netProfit,
      format: 'currency'
    },
    {
      name: 'ROI',
      strategy1Value: metrics1.roi,
      strategy2Value: metrics2.roi,
      format: 'percentage'
    },
    {
      name: 'Win Rate',
      strategy1Value: metrics1.winRate,
      strategy2Value: metrics2.winRate,
      format: 'percentage'
    },
    {
      name: 'Total Bets',
      strategy1Value: metrics1.totalBets,
      strategy2Value: metrics2.totalBets,
      format: 'number'
    },
    {
      name: 'Max Drawdown',
      strategy1Value: metrics1.maxDrawdown,
      strategy2Value: metrics2.maxDrawdown,
      format: 'currency'
    },
    {
      name: 'Sharpe Ratio',
      strategy1Value: metrics1.sharpeRatio,
      strategy2Value: metrics2.sharpeRatio,
      format: 'decimal'
    },
    {
      name: 'Profit Factor',
      strategy1Value: metrics1.profitFactor,
      strategy2Value: metrics2.profitFactor,
      format: 'decimal'
    }
  ];
  
  return comparisonMetrics.map(metric => ({
    ...metric,
    difference: metric.strategy1Value - metric.strategy2Value,
    percentageDifference: metric.strategy2Value !== 0 
      ? ((metric.strategy1Value - metric.strategy2Value) / Math.abs(metric.strategy2Value)) * 100
      : metric.strategy1Value !== 0 ? 100 : 0,
    winner: metric.strategy1Value > metric.strategy2Value ? 'strategy1' : 
            metric.strategy1Value < metric.strategy2Value ? 'strategy2' : 'tie'
  }));
};

/**
 * Calculate statistical significance between two strategies
 * @param {Array} returns1 - Returns from strategy 1
 * @param {Array} returns2 - Returns from strategy 2
 * @returns {Object} Statistical significance test results
 */
export const calculateStatisticalSignificance = (returns1, returns2) => {
  if (!returns1 || !returns2 || returns1.length < 10 || returns2.length < 10) {
    return { significant: false, pValue: null, reason: 'Insufficient data' };
  }
  
  // Simplified t-test calculation
  const mean1 = returns1.reduce((sum, val) => sum + val, 0) / returns1.length;
  const mean2 = returns2.reduce((sum, val) => sum + val, 0) / returns2.length;
  
  const variance1 = returns1.reduce((sum, val) => sum + Math.pow(val - mean1, 2), 0) / (returns1.length - 1);
  const variance2 = returns2.reduce((sum, val) => sum + Math.pow(val - mean2, 2), 0) / (returns2.length - 1);
  
  const pooledVariance = ((returns1.length - 1) * variance1 + (returns2.length - 1) * variance2) / 
                        (returns1.length + returns2.length - 2);
  
  const standardError = Math.sqrt(pooledVariance * (1/returns1.length + 1/returns2.length));
  
  if (standardError === 0) {
    return { significant: false, pValue: null, reason: 'No variance in data' };
  }
  
  const tStatistic = (mean1 - mean2) / standardError;
  
  // Simplified p-value estimation (this would normally require a t-distribution table)
  const pValue = Math.abs(tStatistic) > 2 ? 0.05 : 0.1; // Very simplified
  
  return {
    significant: Math.abs(tStatistic) > 2, // Roughly p < 0.05
    pValue,
    tStatistic,
    degreesOfFreedom: returns1.length + returns2.length - 2
  };
};

/**
 * Generate strategy performance summary
 * @param {Object} strategyResult - Strategy result with performance metrics
 * @returns {Object} Performance summary
 */
export const generatePerformanceSummary = (strategyResult) => {
  const { strategy, performance } = strategyResult;
  
  const summary = {
    strategyName: strategy.name,
    overallRating: calculateOverallRating(performance),
    strengths: [],
    weaknesses: [],
    recommendations: []
  };
  
  // Analyse strengths
  if (performance.roi > 0.1) summary.strengths.push('Strong ROI');
  if (performance.winRate > 0.4) summary.strengths.push('Good win rate');
  if (performance.sharpeRatio > 1) summary.strengths.push('Excellent risk-adjusted returns');
  if (performance.maxDrawdown < performance.netProfit * 0.2) summary.strengths.push('Low drawdown');
  
  // Analyse weaknesses
  if (performance.roi < 0) summary.weaknesses.push('Negative returns');
  if (performance.winRate < 0.2) summary.weaknesses.push('Low win rate');
  if (performance.maxDrawdown > performance.netProfit * 0.5) summary.weaknesses.push('High drawdown');
  if (performance.volatility > 0.3) summary.weaknesses.push('High volatility');
  
  // Generate recommendations
  if (performance.roi < 0) {
    summary.recommendations.push('Consider adjusting tip selections or unit sizing');
  }
  if (performance.maxDrawdown > performance.netProfit * 0.3) {
    summary.recommendations.push('Implement stricter risk management rules');
  }
  if (performance.totalBets < 100) {
    summary.recommendations.push('Test with more data for statistical significance');
  }
  
  return summary;
};

/**
 * Calculate overall strategy rating
 * @param {Object} performance - Performance metrics
 * @returns {string} Rating (A, B, C, D, F)
 */
export const calculateOverallRating = (performance) => {
  let score = 0;
  
  // ROI component (40% weight)
  if (performance.roi > 0.2) score += 40;
  else if (performance.roi > 0.1) score += 30;
  else if (performance.roi > 0.05) score += 20;
  else if (performance.roi > 0) score += 10;
  
  // Win rate component (25% weight)
  if (performance.winRate > 0.5) score += 25;
  else if (performance.winRate > 0.4) score += 20;
  else if (performance.winRate > 0.3) score += 15;
  else if (performance.winRate > 0.2) score += 10;
  
  // Risk management component (25% weight)
  const drawdownRatio = performance.netProfit > 0 ? performance.maxDrawdown / performance.netProfit : 1;
  if (drawdownRatio < 0.2) score += 25;
  else if (drawdownRatio < 0.3) score += 20;
  else if (drawdownRatio < 0.5) score += 15;
  else if (drawdownRatio < 0.7) score += 10;
  
  // Consistency component (10% weight)
  if (performance.sharpeRatio > 1.5) score += 10;
  else if (performance.sharpeRatio > 1) score += 8;
  else if (performance.sharpeRatio > 0.5) score += 6;
  else if (performance.sharpeRatio > 0) score += 4;
  
  // Convert score to letter grade
  if (score >= 85) return 'A';
  if (score >= 70) return 'B';
  if (score >= 55) return 'C';
  if (score >= 40) return 'D';
  return 'F';
};